/**
 * Canonical entity identity.
 *
 * The Strapi v4→v5 migration changed the identifier the REST API resolves an
 * entity by from the numeric `id` to the string `documentId`. Passing the wrong
 * one was the dominant post-migration bug class. Route every REST path segment
 * and relation-connect payload through {@link apiId} so there is exactly one
 * answer to "what is this entity's identity to the API".
 */
import type { Identifiable } from "../types/entities";

/**
 * The identifier the Strapi v5 REST API resolves an entity by (its `documentId`).
 * Use for `/api/:collection/:id` paths and relation-connect payloads.
 *
 * Falls back to the numeric `id` only defensively while producers are still
 * untyped `.js`; once everything is typed the fallback is dead code.
 */
export function apiId(entity: Identifiable | null | undefined): string {
  if (entity == null) return "";
  const id = entity.documentId ?? entity.id;
  // An entity with neither id nor documentId (e.g. an empty {} placeholder) has
  // no identity — return '' rather than the string "undefined", so truthiness
  // checks (e.g. a query `enabled: !!apiId(x)`) correctly treat it as absent.
  return id == null ? "" : String(id);
}

/**
 * A Strapi relation arrives either populated (an object carrying `id`) or as the
 * already-flat scalar id, depending on whether the query populated it. Collapse
 * it to the numeric `id`; a scalar / null / undefined passes through unchanged.
 *
 * Returns the numeric `id`, NOT {@link apiId}'s documentId — this is for local
 * comparisons (e.g. against `ONLINE_RAUM_ID`) and the legacy numeric relation
 * payloads, not REST path segments. Unlike the ad-hoc `typeof x === "object" ?
 * x.id : x` sites it replaced, the leading truthiness check makes it safe
 * against `typeof null === "object"`.
 */
export function relationId<T>(rel: T): T extends object ? number : T {
  const resolved = rel && typeof rel === "object" ? (rel as { id?: number }).id : rel;
  return resolved as T extends object ? number : T;
}
